// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck nx global declarations issue (only on build/serve)

import React from 'react'

import { TableOptions } from 'react-table'
import { get } from 'lodash'
import {
    styled,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableProps as MUITableProps,
} from '@mui/material'
import { useDebounce } from 'use-debounce'
import { useSearchParams } from '@edge/spark/utils'
import { isNonFilterTable } from '../utils/isNonFilterTable'
import { Chart, ChartProps } from '@edge/spark/ui-chart'
import ActionsToolbar, { Props as ActionsToolbarProps } from '../DetailTable/ToolbarWithActions'
import { ToggleShareModal as ShareModal, type ShareMetaData } from '../SharePdfAction/ShareModal'

import { usePushToAmsMutation } from '@edge/spark/io-push-to-ams'
import {
    usePaginatedApolloTable,
    PAGE_SIZE_OPTIONS,
    Props as UsePaginatedApolloTableProps,
} from './usePaginatedApolloTable'
import GlobalFilter from './GlobalFilter'
import TablePaginationActions from './TablePaginationActions'
import { StripedTableRow, useIsMobile } from '@edge/spark/ui-mui'

// TODO: `TableCell`s default style `.MuiTableCell-root` uses `body2`s style
const StyledTableCell = styled(TableCell)`
    font-weight: 400;
    font-size: 16px;
    padding: 4px 16px;
`

type TitleRenderer = ((arg: { count: number; data: any[]; res: any }) => string) | string

const SPACE_COLON_SPACE_REGEX = /\s?:\s?/g

interface Props<D extends Record<string, unknown>>
    extends Omit<MUITableProps, 'title'>,
        Pick<TableOptions<D>, 'columns'>,
        Omit<Partial<TableOptions<D>>, 'columns'>,
        UsePaginatedApolloTableProps<D>,
        Omit<ActionsToolbarProps, 'onShare' | 'title'> {
    chartData?: ChartProps['data']
    chartOnly?: boolean
    mobileColumns?: TableOptions<D>['columns']
    title: TitleRenderer
    sendToAMSPath?: string
    reportHref?: string
    shareMetaData?: ShareMetaData
    isOverviewDetails?: boolean
}

const DetailTable = <D extends Record<string, unknown>>({
    shareMetaData,
    columns,
    mobileColumns,
    countPath,
    lazyQueryHook,
    listPath,
    queryOptions,
    subtitle,
    title,
    chartData,
    chartOnly,
    paginated = true,
    sendToAMSPath,
    isOverviewDetails,
    ...rest
}: Props<D>) => {
    const classes = useStyles()
    const [pushToAms, { loading: loadingAMS }] = usePushToAmsMutation()

    const isMobileScreenWidth = useIsMobile()

    // update gql query with optional custom user filtering from form
    const { filter } = useSearchParams({ filter: '' })
    const [searchValue, setSearchValue] = React.useState(decodeURIComponent(filter))

    const [debounceValue] = useDebounce(searchValue, 600)

    const desktopOrMobileColumns = isMobileScreenWidth && mobileColumns ? mobileColumns : columns
    const {
        count,
        res,
        data,
        getTableBodyProps,
        getTableProps,
        headerGroups,
        loading,
        page,
        prepareRow,
        setPageSize,
        selectedFlatRows,
        state: { pageIndex, pageSize },
        onChangePage,
    } = usePaginatedApolloTable({
        columns: desktopOrMobileColumns,
        countPath,
        lazyQueryHook,
        listPath,
        queryOptions,
        paginated,
        debounceValue,
    })

    const [shareModalOpen, setShareModalOpen] = React.useState(false)
    const [chartAnimationEnded, setChartAnimationEnded] = React.useState(false)
    const chartRef = React.useRef<HTMLDivElement>(null)

    const handleSendToAMS = () => {
        if (!sendToAMSPath) {
            throw new Error('Missing sendToAMSPath')
        }

        const ids = selectedFlatRows.map((d): number => +get(d, `original.${sendToAMSPath}`))
        return pushToAms({ variables: { ids } })
    }

    const renderedTitle = typeof title === 'string' ? title : title({ count, data, res })
    const selectedCount = selectedFlatRows.length
    const isFilterTable = !isNonFilterTable()
    const showTopNav = paginated && (isMobileScreenWidth || pageSize > 10)

    const TableNavigation = ({ top }: { top?: boolean }): JSX.Element => {
        const labelRowsPerPage = isMobileScreenWidth ? 'Rows' : 'Rows per page'
        return (
            <TablePagination
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                component="div"
                count={count}
                rowsPerPage={pageSize}
                page={count ? pageIndex : 0}
                labelRowsPerPage={top ? '' : labelRowsPerPage}
                SelectProps={{
                    sx: { display: top ? 'none' : '' },
                }}
                sx={(theme) => ({
                    [theme.breakpoints.only('xs')]: {
                        flexFlow: 'wrap',
                        '& > p:nth-of-type(2)': {
                            display: 'block',
                            width: '100%',
                        },
                        marginBottom: '8px',
                    },
                })}
                onPageChange={onChangePage}
                onRowsPerPageChange={(evt): void => setPageSize(Number(evt.target.value))}
                ActionsComponent={TablePaginationActions}
            />
        )
    }

    const handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const nextValue = e.currentTarget.value.replaceAll(SPACE_COLON_SPACE_REGEX, ':').replaceAll('?', '')
        setSearchValue(nextValue)
    }

    return (
        <TableContainer component={Paper}>
            <ActionsToolbar
                loading={loading || (chartData && !chartAnimationEnded)}
                loadingAMS={loadingAMS}
                count={selectedCount}
                isOverviewDetails={isOverviewDetails}
                onClickShare={(): void => setShareModalOpen(true)}
                onClickAMS={sendToAMSPath ? handleSendToAMS : undefined}
                subtitle={subtitle}
                title={renderedTitle}
                disabled={loading || !data.length}
            />
            <ShareModal
                secondaryFilter={debounceValue}
                chartOnly={chartOnly}
                chartRef={chartRef}
                columns={columns}
                data={data}
                onClose={(): void => setShareModalOpen(false)}
                open={shareModalOpen}
                subtitle={subtitle}
                title={renderedTitle}
                shareMetaData={shareMetaData}
            />
            {isFilterTable && (
                <GlobalFilter
                    isFiltered={!!searchValue}
                    placeholder="Refine search"
                    searchParams={searchValue}
                    onChangeFilter={handleChangeFilter}
                    onClickClear={(): void => setSearchValue('')}
                    handleSubmit={(e): void => {
                        e.preventDefault()
                    }}
                />
            )}
            {chartData && (
                <div ref={chartRef}>
                    <Chart data={chartData} onAnimationEnd={(): void => setChartAnimationEnded(true)} />
                </div>
            )}
            {!chartOnly && (
                <>
                    {showTopNav && <TableNavigation top />}
                    <Table {...rest} {...getTableProps()}>
                        <TableHead>
                            {headerGroups.map((headerGroup) => (
                                <TableRow {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <TableCell {...column.getHeaderProps()}>{column.render('Header')}</TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody {...getTableBodyProps()}>
                            {data.length ? (
                                page.map((row, i) => {
                                    prepareRow(row)
                                    return (
                                        <StripedTableRow {...row.getRowProps()}>
                                            {row.cells.map((cell) => (
                                                <StyledTableCell
                                                    {...cell.getCellProps()}
                                                    sx={
                                                        isOverviewDetails && i === 0
                                                            ? {
                                                                  fontWeight: 500,
                                                                  borderBottomWidth: 'medium',
                                                                  backgroundColor: 'rgba(0, 0, 0, 3.5%)',
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    {cell.render('Cell')}
                                                </StyledTableCell>
                                            ))}
                                        </StripedTableRow>
                                    )
                                })
                            ) : (
                                <TableRow>
                                    <TableCell align="center" colSpan={999}>
                                        {loading ? 'Loading...' : 'No Data Found.'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {paginated && <TableNavigation />}
                </>
            )}
        </TableContainer>
    )
}

export const ReactTable = DetailTable
